import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  ArrowLeft, Send, Sprout, Loader2, Download, Copy, RefreshCw,
  BookOpen, Target, Lightbulb, FlaskConical, Pencil, CheckCircle2,
  Leaf, GraduationCap, FileText, Printer, ChevronDown, ChevronUp,
  Sparkles, MessageCircle, Edit3, Save, X, Key, Plus, Trash2
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Sanitize HTML content for safe rendering
const sanitizeHTML = (html) => DOMPurify.sanitize(html, { ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'p', 'br', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'div', 'span', 'table', 'tr', 'td', 'th'] });

// Helper to safely render any value as a string (handles nested objects from AI)
const safeRender = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) {
    return value.map(v => safeRender(v)).join(", ");
  }
  if (typeof value === "object") {
    // Try common keys that might contain the main text
    const textKeys = ['text', 'content', 'description', 'value', 'word', 'question', 'title', 'name', 'skill'];
    for (const key of textKeys) {
      if (value[key] && typeof value[key] === 'string') return value[key];
    }
    // Fall back to first string value found, or JSON representation
    const values = Object.values(value).filter(v => typeof v === 'string');
    if (values.length > 0) return values.join(' - ');
    return JSON.stringify(value).slice(0, 200);
  }
  return String(value);
};

// 5E Phase icons and colors
const PHASE_CONFIG = {
  engage: { icon: Lightbulb, color: "text-yellow-600", bg: "bg-yellow-50", border: "border-yellow-200", label: "ENGAGE" },
  explore: { icon: FlaskConical, color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-200", label: "EXPLORE" },
  explain: { icon: BookOpen, color: "text-purple-600", bg: "bg-purple-50", border: "border-purple-200", label: "EXPLAIN" },
  elaborate: { icon: Pencil, color: "text-green-600", bg: "bg-green-50", border: "border-green-200", label: "ELABORATE" },
  evaluate: { icon: CheckCircle2, color: "text-red-600", bg: "bg-red-50", border: "border-red-200", label: "EVALUATE" }
};

const GrowthToolPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [currentLessonPlan, setCurrentLessonPlan] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  // Editable worksheets state
  const [editMode, setEditMode] = useState(false);
  const [editedWorksheets, setEditedWorksheets] = useState(null);

  // Initial welcome message
  useEffect(() => {
    setMessages([{
      id: 1,
      role: "assistant",
      content: "Welcome to the Growth Tool! I'm here to help you create comprehensive SAE 5E lesson plans and GROWTH challenges.\n\nTell me:\n• What **standard** you want to cover (CCSS, NGSS, VISAE, etc.)\n• What **topic or idea** you have in mind\n• Your **grade level**\n\nI'll generate a complete lesson plan with all 5E phases, materials, vocabulary, and assessments!",
      timestamp: new Date()
    }]);
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // State for streaming content
  const [streamingContent, setStreamingContent] = useState("");

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      role: "user",
      content: input.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);
    setStreamingContent("");

    // Add placeholder for streaming assistant message
    const assistantMessageId = Date.now() + 1;
    setMessages(prev => [...prev, {
      id: assistantMessageId,
      role: "assistant",
      content: "",
      isStreaming: true,
      timestamp: new Date()
    }]);

    try {
      // Use streaming endpoint
      const response = await fetch(`${API}/growth-tool/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage.content,
          conversation_history: messages.map(m => ({ role: m.role, content: m.content })),
          current_lesson_plan: currentLessonPlan
        }),
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedContent = "";
      let finalLessonPlan = null;
      let finalMessage = "";
      let buffer = ""; // Buffer for incomplete lines across chunks

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const text = decoder.decode(value, { stream: true });
        buffer += text; // Append to buffer
        
        const lines = buffer.split('\n');
        // Keep the last element as it may be incomplete
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'start') {
                // Update streaming message with initial text
                setMessages(prev => prev.map(m => 
                  m.id === assistantMessageId 
                    ? { ...m, content: "🌱 " + data.message }
                    : m
                ));
              } else if (data.type === 'progress') {
                // Show progress updates with animation
                setMessages(prev => prev.map(m => 
                  m.id === assistantMessageId 
                    ? { ...m, content: "🌱 " + data.message }
                    : m
                ));
              } else if (data.type === 'chunk') {
                accumulatedContent += data.content;
                // Update the streaming message content progressively
                // Show a preview of the raw content being generated
                const preview = accumulatedContent.length > 200 
                  ? "..." + accumulatedContent.slice(-200)
                  : accumulatedContent;
                setMessages(prev => prev.map(m => 
                  m.id === assistantMessageId 
                    ? { ...m, content: `🌱 Generating lesson plan...\n\n\`\`\`\n${preview}\n\`\`\`` }
                    : m
                ));
                setStreamingContent(accumulatedContent);
              } else if (data.type === 'complete') {
                finalLessonPlan = data.lesson_plan;
                finalMessage = data.message;
              } else if (data.type === 'error') {
                throw new Error(data.message);
              }
            } catch (parseError) {
              // Ignore parse errors for incomplete JSON chunks
              if (line.trim() && !line.includes('[DONE]')) {
                console.log("Parse skip:", line.slice(0, 50));
              }
            }
          }
        }
      }
      
      // Process any remaining data in buffer after stream ends
      if (buffer.startsWith('data: ')) {
        try {
          const data = JSON.parse(buffer.slice(6));
          if (data.type === 'complete') {
            finalLessonPlan = data.lesson_plan;
            finalMessage = data.message;
          }
        } catch (e) {
          console.log("Final buffer parse skip:", buffer.slice(0, 50));
        }
      }

      // Update the final message with parsed content
      setMessages(prev => prev.map(m => 
        m.id === assistantMessageId 
          ? { 
              ...m, 
              content: finalMessage || "Here's your lesson plan!", 
              lesson_plan: finalLessonPlan,
              isStreaming: false 
            }
          : m
      ));
      
      if (finalLessonPlan) {
        setCurrentLessonPlan(finalLessonPlan);
        initializeEditableWorksheets(finalLessonPlan);
        setExpandedSections({
          overview: true,
          engage: true,
          explore: true,
          explain: true,
          elaborate: true,
          evaluate: true,
          materials: true,
          assessment: true
        });
        toast.success("Lesson plan generated!");
      }
      
    } catch (error) {
      console.error("Stream error:", error);
      let errorContent = "I apologize, but I encountered an error generating your lesson plan. Please try again.";
      
      if (error.message?.includes('timeout') || error.name === 'AbortError') {
        errorContent = "The lesson plan is taking longer than expected. Please try again with a simpler request.";
      }
      
      // Update the streaming message to show error
      setMessages(prev => prev.map(m => 
        m.id === (Date.now() + 1) || m.isStreaming
          ? { ...m, content: errorContent, isStreaming: false }
          : m
      ));
      toast.error("Generation failed. Please try again.");
    } finally {
      setIsLoading(false);
      setStreamingContent("");
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const copyToClipboard = () => {
    if (!currentLessonPlan) return;
    
    const text = formatLessonPlanAsText(currentLessonPlan);
    navigator.clipboard.writeText(text);
    toast.success("Lesson plan copied to clipboard!");
  };

  // Initialize editable worksheets from lesson plan
  const initializeEditableWorksheets = (plan) => {
    setEditedWorksheets({
      check_your_roots: {
        agriculture_connection_question: plan.check_your_roots?.agriculture_connection_question || `How is ${plan.title || 'this topic'} related to Agriculture?`,
        grow_your_knowledge_directions: plan.check_your_roots?.grow_your_knowledge_directions || "Answer the following questions based on today's lesson:",
        grow_your_knowledge_questions: plan.check_your_roots?.grow_your_knowledge_questions || [
          "What did you learn today about this topic?",
          "How does this connect to sustainable agriculture?",
          "What questions do you still have?"
        ],
        local_sae_experience: plan.check_your_roots?.local_sae_experience || "",
        home_learning_directions: plan.check_your_roots?.home_learning_directions || "Complete the following at home:",
        home_learning_prompts: plan.check_your_roots?.home_learning_prompts || [
          "Discuss what you learned with a family member",
          "Find an example of this concept in your community"
        ]
      },
      creative_worksheet: {
        directions: plan.creative_worksheet?.directions || "Use the space below to draw, design, or create something related to today's lesson:",
        prompts: plan.creative_worksheet?.prompts || [
          "Draw a diagram showing what you learned",
          "Design your own garden using today's concepts"
        ],
        vocabulary_practice: typeof plan.creative_worksheet?.vocabulary_practice === 'object' 
          ? plan.creative_worksheet.vocabulary_practice.directions || "Match the vocabulary words to their definitions."
          : (plan.creative_worksheet?.vocabulary_practice || "Use each vocabulary word in a sentence about agriculture."),
        vocabulary_pairs: typeof plan.creative_worksheet?.vocabulary_practice === 'object' 
          ? plan.creative_worksheet.vocabulary_practice.pairs || []
          : []
      },
      test_your_growth: {
        instructions: plan.test_your_growth?.instructions || "Answer all questions to the best of your ability. Show your work where applicable.",
        multiple_choice: plan.test_your_growth?.multiple_choice || [
          { question: "Sample question 1?", options: ["Option A", "Option B", "Option C", "Option D"], correct_answer: "A" },
          { question: "Sample question 2?", options: ["Option A", "Option B", "Option C", "Option D"], correct_answer: "B" }
        ],
        short_answer: plan.test_your_growth?.short_answer || [
          "Explain in your own words what you learned today."
        ],
        extended_response: plan.test_your_growth?.extended_response || "How does this lesson connect to sustainable agriculture and why does it matter for our community?"
      }
    });
    setEditMode(false);
  };

  // Update a specific field in editable worksheets
  const updateWorksheetField = (section, field, value) => {
    setEditedWorksheets(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  // Add a question to a section
  const addQuestion = (section, field) => {
    setEditedWorksheets(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: [...(prev[section][field] || []), ""]
      }
    }));
  };

  // Remove a question from a section
  const removeQuestion = (section, field, index) => {
    setEditedWorksheets(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: prev[section][field].filter((_, i) => i !== index)
      }
    }));
  };

  // Update a specific question in an array
  const updateQuestion = (section, field, index, value) => {
    setEditedWorksheets(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: prev[section][field].map((q, i) => i === index ? value : q)
      }
    }));
  };

  // Update multiple choice question
  const updateMCQuestion = (index, field, value) => {
    setEditedWorksheets(prev => ({
      ...prev,
      test_your_growth: {
        ...prev.test_your_growth,
        multiple_choice: prev.test_your_growth.multiple_choice.map((q, i) => 
          i === index ? { ...q, [field]: value } : q
        )
      }
    }));
  };

  // Add multiple choice question
  const addMCQuestion = () => {
    setEditedWorksheets(prev => ({
      ...prev,
      test_your_growth: {
        ...prev.test_your_growth,
        multiple_choice: [
          ...prev.test_your_growth.multiple_choice,
          { question: "", options: ["", "", "", ""], correct_answer: "A" }
        ]
      }
    }));
  };

  // Remove multiple choice question
  const removeMCQuestion = (index) => {
    setEditedWorksheets(prev => ({
      ...prev,
      test_your_growth: {
        ...prev.test_your_growth,
        multiple_choice: prev.test_your_growth.multiple_choice.filter((_, i) => i !== index)
      }
    }));
  };

  // Save edits
  const saveWorksheetEdits = () => {
    setEditMode(false);
    toast.success("Worksheet edits saved!");
  };

  // Print Answer Key
  const printAnswerKey = () => {
    if (!currentLessonPlan || !editedWorksheets) return;
    
    const worksheets = editedWorksheets;
    const printWindow = window.open("", "_blank");
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Answer Key - ${currentLessonPlan.title || "Lesson Plan"}</title>
        <style>
          body { font-family: Georgia, serif; max-width: 800px; margin: 0 auto; padding: 40px; line-height: 1.6; }
          h1 { color: #2d5016; border-bottom: 3px solid #2d5016; padding-bottom: 10px; }
          h2 { color: #4a7c23; margin-top: 30px; }
          h3 { color: #666; background: #f5f5f5; padding: 10px; border-radius: 5px; }
          .answer { background: #e8f5e9; padding: 10px 15px; margin: 10px 0; border-left: 4px solid #4caf50; border-radius: 0 8px 8px 0; }
          .answer strong { color: #2d5016; }
          .question { margin: 15px 0; padding: 10px; background: #fafafa; border-radius: 8px; }
          .mc-options { margin-left: 20px; }
          .correct { color: #4caf50; font-weight: bold; }
          .watermark { position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(-45deg); font-size: 100px; color: rgba(45, 80, 22, 0.1); z-index: -1; pointer-events: none; }
          @media print { .watermark { display: block; } }
        </style>
      </head>
      <body>
        <div class="watermark">ANSWER KEY</div>
        
        <h1>🔑 TEACHER ANSWER KEY</h1>
        <p><strong>Lesson:</strong> ${currentLessonPlan.title || "Untitled"}</p>
        <p><strong>Standard:</strong> ${currentLessonPlan.standard || "N/A"}</p>
        <p><strong>Grade Level:</strong> ${currentLessonPlan.grade_level || "N/A"}</p>
        <hr style="margin: 20px 0;" />
        
        <h2>📝 TEST YOUR GROWTH - Answer Key</h2>
        
        <h3>Multiple Choice Answers</h3>
        ${worksheets.test_your_growth.multiple_choice.map((q, i) => `
          <div class="question">
            <p><strong>${i + 1}.</strong> ${q.question}</p>
            <div class="mc-options">
              ${q.options.map((opt, j) => `
                <p class="${String.fromCharCode(65 + j) === q.correct_answer ? 'correct' : ''}">
                  ${String.fromCharCode(65 + j)}. ${opt} ${String.fromCharCode(65 + j) === q.correct_answer ? '✓ CORRECT' : ''}
                </p>
              `).join('')}
            </div>
            <div class="answer">
              <strong>Answer:</strong> ${q.correct_answer}
            </div>
          </div>
        `).join('')}
        
        <h3>Short Answer - Sample Responses</h3>
        ${worksheets.test_your_growth.short_answer.map((q, i) => `
          <div class="question">
            <p><strong>${i + 1}.</strong> ${q}</p>
            <div class="answer">
              <strong>Expected Response:</strong> Students should demonstrate understanding of the key concepts covered in the lesson. 
              Look for use of vocabulary words and connections to agriculture.
            </div>
          </div>
        `).join('')}
        
        <h3>Extended Response - Rubric</h3>
        <div class="question">
          <p><strong>Question:</strong> ${worksheets.test_your_growth.extended_response}</p>
          <div class="answer">
            <strong>Scoring Rubric:</strong>
            <ul>
              <li><strong>4 points:</strong> Complete, detailed response with clear connections to agriculture, uses vocabulary correctly, provides specific examples</li>
              <li><strong>3 points:</strong> Good response with some connections to agriculture, mostly correct vocabulary use</li>
              <li><strong>2 points:</strong> Partial response, limited connections or vocabulary use</li>
              <li><strong>1 point:</strong> Minimal response, unclear or incorrect</li>
            </ul>
          </div>
        </div>
        
        <h2>🌱 CHECK YOUR ROOTS - Discussion Points</h2>
        <div class="question">
          <p><strong>Agriculture Connection:</strong> ${worksheets.check_your_roots.agriculture_connection_question}</p>
          <div class="answer">
            <strong>Key Points:</strong> Look for students connecting the lesson content to sustainable agriculture, local farming practices, or food systems.
          </div>
        </div>
        
        <h3>Grow Your Knowledge - Expected Answers</h3>
        ${worksheets.check_your_roots.grow_your_knowledge_questions.map((q, i) => `
          <div class="question">
            <p><strong>${i + 1}.</strong> ${q}</p>
            <div class="answer">
              <strong>Look for:</strong> Understanding of key concepts, use of vocabulary, personal connections to content.
            </div>
          </div>
        `).join('')}
        
        <h2>📚 Vocabulary Words</h2>
        <div class="answer">
          ${currentLessonPlan.vocabulary ? currentLessonPlan.vocabulary.map(v => `<p>• <strong>${typeof v === 'object' ? v.word : v}</strong>${typeof v === 'object' && v.definition ? `: ${v.definition}` : ''}</p>`).join('') : '<p>See lesson plan vocabulary section</p>'}
        </div>
        
        <footer style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; text-align: center; color: #888;">
          Generated by ROOTSPACE Growth Tool | For Teacher Use Only
        </footer>
        
        <script>window.print();</script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const formatLessonPlanAsText = (plan) => {
    return `
SAE 5E LESSON PLAN | GROW PLAN
==============================

Instructor: ${plan.instructor || "_____________"}
Grade: ${plan.grade_level || "ALL GRADE LEVELS"}
Course: ${plan.course || "_____________"}
Date: ${plan.date || "_____________"}

CONTENT AREA AND SAE CONNECTION: ${plan.content_area || ""}
${plan.sae_connection || ""}

VISAE FOUNDATION STANDARDS:
${plan.visae_standards?.map(s => `[X] ${s}`).join("\n") || ""}

KEY CONCEPTS (Fruit):
KEY CONCEPTS (Fruit):
${typeof plan.key_concepts === 'object' ? JSON.stringify(plan.key_concepts) : (plan.key_concepts || "")}

SUSTAINABLE OBJECTIVE (Fruit):
Students will be able to: ${plan.sustainable_objective || ""}

KEY VOCABULARY (Seasonings):
${plan.vocabulary?.map(v => typeof v === 'object' ? v.word : v).join(", ") || ""}

CONTENT OBJECTIVE:
Students will be able to: ${plan.content_objective || ""}

=== 5E PHASES ===

ENGAGE:
${plan.engage || ""}

EXPLORE:
${plan.explore || ""}

EXPLAIN:
${plan.explain || ""}

ELABORATE:
${plan.elaborate || ""}

EVALUATE:
${plan.evaluate || ""}

MATERIALS & TOOLS:
${plan.materials?.join("\n") || ""}

IN-CLASS SAE LAB:
${plan.sae_lab || ""}

HOME LEARNING:
${plan.home_learning || ""}

ASSESSMENT:
${typeof plan.assessment === 'object' ? (plan.assessment.rubric_type || '') + ' - ' + (plan.assessment.criteria?.map(c => typeof c === 'object' ? c.skill : c).join(', ') || '') : (plan.assessment || "")}
    `.trim();
  };

  const downloadAsPDF = () => {
    if (!currentLessonPlan) return;
    
    // Use edited worksheets if available, otherwise fall back to lesson plan
    const worksheets = editedWorksheets || {
      check_your_roots: currentLessonPlan.check_your_roots || {},
      creative_worksheet: currentLessonPlan.creative_worksheet || {},
      test_your_growth: currentLessonPlan.test_your_growth || {}
    };
    
    // Create a printable version
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>SAE 5E Lesson Plan - ${currentLessonPlan.title || "Growth Plan"}</title>
          <style>
            body { font-family: Georgia, serif; max-width: 800px; margin: 0 auto; padding: 40px; line-height: 1.6; }
            h1 { color: #2d5016; border-bottom: 3px solid #2d5016; padding-bottom: 10px; }
            h2 { color: #4a7c23; margin-top: 30px; }
            h3 { color: #666; }
            .header-info { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin: 20px 0; }
            .header-info div { border-bottom: 1px solid #ddd; padding: 5px 0; }
            .phase { margin: 20px 0; padding: 15px; border-radius: 8px; }
            .engage { background: #fef9c3; border-left: 4px solid #eab308; }
            .explore { background: #dbeafe; border-left: 4px solid #3b82f6; }
            .explain { background: #f3e8ff; border-left: 4px solid #9333ea; }
            .elaborate { background: #dcfce7; border-left: 4px solid #22c55e; }
            .evaluate { background: #fee2e2; border-left: 4px solid #ef4444; }
            .vocabulary { display: flex; flex-wrap: wrap; gap: 8px; }
            .vocab-word { background: #e8f5e9; padding: 4px 12px; border-radius: 20px; font-size: 14px; }
            .materials-list { columns: 2; }
            .checkbox { margin-right: 8px; }
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          <h1>SAE 5E Lesson Plan | Grow Plan</h1>
          
          <div class="header-info">
            <div><strong>Instructor:</strong> ${currentLessonPlan.instructor || "_____________"}</div>
            <div><strong>Grade:</strong> ${currentLessonPlan.grade_level || "ALL GRADE LEVELS"}</div>
            <div><strong>Course:</strong> ${currentLessonPlan.course || "_____________"}</div>
            <div><strong>Date:</strong> ${currentLessonPlan.date || "_____________"}</div>
          </div>
          
          <h2>Content Area and SAE Connection</h2>
          <p><strong>${currentLessonPlan.content_area || ""}</strong></p>
          <p>${currentLessonPlan.sae_connection || ""}</p>
          
          <h2>VISAE Foundation Standards</h2>
          <ul>
            ${currentLessonPlan.visae_standards?.map(s => `<li>${s}</li>`).join("") || ""}
          </ul>
          
          <h2>Key Concepts (Fruit)</h2>
          <p>${typeof currentLessonPlan.key_concepts === 'object' ? JSON.stringify(currentLessonPlan.key_concepts) : (currentLessonPlan.key_concepts || "")}</p>
          
          <h2>Sustainable Objective (Fruit)</h2>
          <p><strong>Students will be able to:</strong> ${currentLessonPlan.sustainable_objective || ""}</p>
          
          <h2>Key Vocabulary (Seasonings)</h2>
          <div class="vocabulary">
            ${currentLessonPlan.vocabulary?.map(v => `<span class="vocab-word">${typeof v === 'object' ? v.word : v}</span>`).join("") || ""}
          </div>
          
          <h2>Content Objective</h2>
          <p><strong>Students will be able to:</strong> ${currentLessonPlan.content_objective || ""}</p>
          
          <h1 style="margin-top: 40px;">5E Instructional Phases</h1>
          
          <div class="phase engage">
            <h3>ENGAGE</h3>
            <p>${currentLessonPlan.engage || ""}</p>
          </div>
          
          <div class="phase explore">
            <h3>EXPLORE</h3>
            <p>${currentLessonPlan.explore || ""}</p>
          </div>
          
          <div class="phase explain">
            <h3>EXPLAIN</h3>
            <p>${currentLessonPlan.explain || ""}</p>
          </div>
          
          <div class="phase elaborate">
            <h3>ELABORATE</h3>
            <p>${currentLessonPlan.elaborate || ""}</p>
          </div>
          
          <div class="phase evaluate">
            <h3>EVALUATE</h3>
            <p>${currentLessonPlan.evaluate || ""}</p>
          </div>
          
          <h2>Materials & Tools</h2>
          <div class="materials-list">
            ${currentLessonPlan.materials?.map(m => `<div><span class="checkbox">☐</span>${m}</div>`).join("") || ""}
          </div>
          
          <h2>In-Class SAE Lab</h2>
          <p>${currentLessonPlan.sae_lab || ""}</p>
          
          <h2>Home Learning</h2>
          <p>${currentLessonPlan.home_learning || ""}</p>
          
          <h2>Assessment (Grow)</h2>
          <p>${typeof currentLessonPlan.assessment === 'object' 
            ? (currentLessonPlan.assessment.rubric_type || 'Assessment') + (currentLessonPlan.assessment.criteria ? ': ' + currentLessonPlan.assessment.criteria.map(c => typeof c === 'object' ? c.skill : c).join(', ') : '')
            : (currentLessonPlan.assessment || "")}</p>
          
          <!-- PAGE BREAK - Student Worksheet 1: Check Your Roots -->
          <div style="page-break-before: always; margin-top: 40px;">
            <h1 style="text-align: center; border: 3px solid #2d5016; padding: 20px; margin-bottom: 30px;">
              🌱 CHECK YOUR ROOTS
            </h1>
            
            <div style="border: 1px solid #ddd; padding: 15px; margin-bottom: 20px;">
              <p><strong>Name:</strong> ________________________________</p>
              <p><strong>Date:</strong> _________________ <strong>Class:</strong> _________________</p>
            </div>
            
            <h3>How is ${currentLessonPlan.title || 'this subject'} related to Agriculture?</h3>
            ${worksheets.check_your_roots?.agriculture_connection_question ? 
              `<p>${worksheets.check_your_roots.agriculture_connection_question}</p>` : 
              '<div style="border-bottom: 1px solid #ccc; height: 60px; margin: 10px 0;"></div>'}
            
            <h3 style="background: #e8f5e9; padding: 10px; margin-top: 30px;">🌿 GROW YOUR KNOWLEDGE</h3>
            <p><em>${worksheets.check_your_roots?.grow_your_knowledge_directions || 'Answer the following questions based on today\'s lesson:'}</em></p>
            ${worksheets.check_your_roots?.grow_your_knowledge_questions ? 
              worksheets.check_your_roots.grow_your_knowledge_questions.map((q, i) => 
                `<p>${i + 1}. ${q}</p><div style="border-bottom: 1px solid #ccc; height: 40px; margin: 5px 0 15px 0;"></div>`
              ).join('') : 
              `<div style="border-bottom: 1px solid #ccc; height: 50px; margin: 10px 0;"></div>
               <div style="border-bottom: 1px solid #ccc; height: 50px; margin: 10px 0;"></div>
               <div style="border-bottom: 1px solid #ccc; height: 50px; margin: 10px 0;"></div>`}
            
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-top: 30px;">
              <div style="border: 2px solid #4a7c23; padding: 15px; border-radius: 8px;">
                <h4>🏡 LOCAL SAE EXPERIENCES</h4>
                <p style="font-size: 12px;">Write the time and date of a related Supervised Ag Experience or Ag Event:</p>
                ${worksheets.check_your_roots?.local_sae_experience ? 
                  `<p>${worksheets.check_your_roots.local_sae_experience}</p>` : ''}
                <div style="border-bottom: 1px solid #ccc; height: 30px; margin: 5px 0;"></div>
                <div style="border-bottom: 1px solid #ccc; height: 30px; margin: 5px 0;"></div>
              </div>
              
              <div style="border: 2px solid #2196F3; padding: 15px; border-radius: 8px;">
                <h4>📝 VOCABULARY</h4>
                ${currentLessonPlan.vocabulary ? 
                  currentLessonPlan.vocabulary.slice(0, 5).map(v => `<p>• ${typeof v === 'object' ? v.word : v}</p>`).join('') : 
                  '<p>• ____________</p><p>• ____________</p><p>• ____________</p>'}
              </div>
            </div>
            
            <div style="margin-top: 30px; border: 2px solid #9c27b0; padding: 15px; border-radius: 8px;">
              <h4>🏠 HOME LEARNING: Writing, Planning, Modeling Zone</h4>
              <p><em>${worksheets.check_your_roots?.home_learning_directions || 'Complete the following at home:'}</em></p>
              ${worksheets.check_your_roots?.home_learning_prompts ? 
                worksheets.check_your_roots.home_learning_prompts.map((p, i) => 
                  `<p>${i + 1}. ${p}</p>`
                ).join('') : 
                `<div style="border-bottom: 1px solid #ccc; height: 60px; margin: 10px 0;"></div>`}
            </div>
          </div>
          
          <!-- PAGE BREAK - Student Worksheet 2: Creative Activity -->
          <div style="page-break-before: always; margin-top: 40px;">
            <h1 style="text-align: center; border: 3px solid #ff9800; padding: 20px; margin-bottom: 30px;">
              🎨 CHECK YOUR ROOTS: Think It, Draw It, Create It!
            </h1>
            
            <div style="border: 1px solid #ddd; padding: 15px; margin-bottom: 20px;">
              <p><strong>Name:</strong> ________________________________</p>
              <p><strong>Date:</strong> _________________ <strong>Class:</strong> _________________</p>
            </div>
            
            <h3 style="background: #fff3e0; padding: 10px;">✏️ CREATIVE ACTIVITY</h3>
            <p><em>${worksheets.creative_worksheet?.directions || 'Use the space below to draw, design, or create something related to today\'s lesson:'}</em></p>
            
            ${worksheets.creative_worksheet?.prompts ? 
              worksheets.creative_worksheet.prompts.map((p, i) => 
                `<p><strong>${i + 1}.</strong> ${p}</p>`
              ).join('') : ''}
            
            <div style="border: 2px dashed #ccc; height: 300px; margin: 20px 0; display: flex; align-items: center; justify-content: center;">
              <p style="color: #999; font-style: italic;">Drawing / Design Space</p>
            </div>
            
            <h3 style="background: #e3f2fd; padding: 10px; margin-top: 30px;">📚 VOCABULARY PRACTICE</h3>
            <p><em>${typeof worksheets.creative_worksheet?.vocabulary_practice === 'object' 
              ? (worksheets.creative_worksheet.vocabulary_practice.directions || 'Match the vocabulary words to their definitions:')
              : (worksheets.creative_worksheet?.vocabulary_practice || 'Match the vocabulary words to their definitions or use them in sentences:')}</em></p>
            ${typeof worksheets.creative_worksheet?.vocabulary_practice === 'object' && worksheets.creative_worksheet.vocabulary_practice.pairs 
              ? worksheets.creative_worksheet.vocabulary_practice.pairs.map((p, i) => `<p style="margin: 5px 0;">${i+1}. <strong>${p.word}</strong>: _____________</p>`).join('')
              : ''}
            <div style="border-bottom: 1px solid #ccc; height: 100px; margin: 10px 0;"></div>
          </div>
          
          <!-- PAGE BREAK - Student Worksheet 3: Test Your Growth -->
          <div style="page-break-before: always; margin-top: 40px;">
            <h1 style="text-align: center; border: 3px solid #f44336; padding: 20px; margin-bottom: 30px;">
              📝 TEST YOUR GROWTH
            </h1>
            
            <div style="border: 1px solid #ddd; padding: 15px; margin-bottom: 20px;">
              <p><strong>Name:</strong> ________________________________</p>
              <p><strong>Date:</strong> _________________ <strong>Class:</strong> _________________</p>
            </div>
            
            <p style="background: #ffebee; padding: 10px; border-radius: 8px;">
              <strong>Instructions:</strong> ${worksheets.test_your_growth?.instructions || 'Answer all questions to the best of your ability. Show your work where applicable.'}
            </p>
            
            <h3 style="margin-top: 30px;">Multiple Choice (Circle the correct answer)</h3>
            ${worksheets.test_your_growth?.multiple_choice ? 
              worksheets.test_your_growth.multiple_choice.map((q, i) => `
                <p><strong>${i + 1}.</strong> ${q.question || q}</p>
                ${q.options ? q.options.map((opt, j) => `<p style="margin-left: 20px;">${String.fromCharCode(65 + j)}. ${opt}</p>`).join('') : 
                  '<p style="margin-left: 20px;">A. ____________  B. ____________  C. ____________  D. ____________</p>'}
              `).join('') : 
              `<p>1. ________________________________________________________________</p>
               <p style="margin-left: 20px;">A. ____________  B. ____________  C. ____________  D. ____________</p>
               <p>2. ________________________________________________________________</p>
               <p style="margin-left: 20px;">A. ____________  B. ____________  C. ____________  D. ____________</p>`}
            
            <h3 style="margin-top: 30px;">Short Answer</h3>
            ${worksheets.test_your_growth?.short_answer ? 
              worksheets.test_your_growth.short_answer.map((q, i) => `
                <p><strong>${i + 1}.</strong> ${q}</p>
                <div style="border-bottom: 1px solid #ccc; height: 50px; margin: 5px 0 20px 0;"></div>
              `).join('') : 
              `<p>1. ________________________________________________________________</p>
               <div style="border-bottom: 1px solid #ccc; height: 50px; margin: 5px 0 20px 0;"></div>`}
            
            <h3 style="margin-top: 30px;">Extended Response</h3>
            <p><strong>${worksheets.test_your_growth?.extended_response || 'Explain how this lesson connects to sustainable agriculture and why it matters for our community.'}</strong></p>
            <div style="border: 1px solid #ccc; height: 150px; margin: 10px 0;"></div>
          </div>
          
          <script>window.print();</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const downloadAsJSON = () => {
    if (!currentLessonPlan) return;
    
    const dataStr = JSON.stringify(currentLessonPlan, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `lesson-plan-${currentLessonPlan.title?.replace(/[^a-z0-9]/gi, '-') || 'untitled'}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success("Lesson plan downloaded as JSON!");
  };

  const downloadAsWord = () => {
    if (!currentLessonPlan) return;
    
    const plan = currentLessonPlan;
    const worksheets = editedWorksheets || {
      check_your_roots: plan.check_your_roots || {},
      creative_worksheet: plan.creative_worksheet || {},
      test_your_growth: plan.test_your_growth || {}
    };
    
    // Create HTML content that Word can open
    const htmlContent = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word">
      <head><meta charset="UTF-8"><title>${plan.title || 'SAE 5E Lesson Plan'}</title></head>
      <body style="font-family: Georgia, serif; max-width: 800px; margin: 0 auto; padding: 40px;">
        <h1 style="color: #2d5016; border-bottom: 3px solid #2d5016;">${plan.title || 'SAE 5E Lesson Plan'}</h1>
        
        <table style="width: 100%; margin: 20px 0;">
          <tr><td><strong>Grade:</strong> ${plan.grade_level || ''}</td><td><strong>Content Area:</strong> ${plan.content_area || ''}</td></tr>
          <tr><td><strong>Standard:</strong> ${plan.standard || ''}</td><td><strong>Date:</strong> ____________</td></tr>
        </table>
        
        <h2 style="color: #4a7c23;">Key Concepts</h2>
        <p>${safeRender(plan.key_concepts)}</p>
        
        <h2 style="color: #4a7c23;">Sustainable Objective</h2>
        <p><strong>Students will be able to:</strong> ${safeRender(plan.sustainable_objective)}</p>
        
        <h2 style="color: #4a7c23;">Key Vocabulary</h2>
        <p>${plan.vocabulary?.map(v => typeof v === 'object' ? v.word : v).join(', ') || ''}</p>
        
        <h2 style="color: #eab308;">ENGAGE</h2>
        <p>${safeRender(plan.engage)}</p>
        
        <h2 style="color: #3b82f6;">EXPLORE</h2>
        <p>${safeRender(plan.explore)}</p>
        
        <h2 style="color: #9333ea;">EXPLAIN</h2>
        <p>${safeRender(plan.explain)}</p>
        
        <h2 style="color: #22c55e;">ELABORATE</h2>
        <p>${safeRender(plan.elaborate)}</p>
        
        <h2 style="color: #ef4444;">EVALUATE</h2>
        <p>${safeRender(plan.evaluate)}</p>
        
        <h2 style="color: #4a7c23;">Materials</h2>
        <ul>${plan.materials?.map(m => `<li>${safeRender(m)}</li>`).join('') || ''}</ul>
        
        <h2 style="color: #4a7c23;">Assessment</h2>
        <p>${safeRender(plan.assessment)}</p>
      </body>
      </html>
    `;
    
    const blob = new Blob([htmlContent], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `lesson-plan-${plan.title?.replace(/[^a-z0-9]/gi, '-') || 'untitled'}.doc`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success("Lesson plan downloaded as Word document!");
  };

  const startNewPlan = () => {
    setCurrentLessonPlan(null);
    setMessages([{
      id: Date.now(),
      role: "assistant",
      content: "Let's create a new lesson plan! Tell me:\n• What **standard** you want to cover\n• What **topic or idea** you have in mind\n• Your **grade level**",
      timestamp: new Date()
    }]);
    setExpandedSections({});
  };

  // Render a 5E phase section
  const renderPhaseSection = (phase, content) => {
    if (!content) return null;
    const config = PHASE_CONFIG[phase];
    const Icon = config.icon;
    const isExpanded = expandedSections[phase];

    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={`border rounded-lg overflow-hidden ${config.border}`}
      >
        <button
          onClick={() => toggleSection(phase)}
          className={`w-full flex items-center justify-between p-3 ${config.bg} hover:opacity-90 transition-opacity`}
        >
          <div className="flex items-center gap-2">
            <Icon className={`w-5 h-5 ${config.color}`} />
            <span className={`font-semibold ${config.color}`}>{config.label}</span>
          </div>
          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="p-4 bg-white"
            >
              <p className="text-gray-700 whitespace-pre-wrap">{content}</p>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  // Render lesson plan card
  const renderLessonPlan = (plan) => {
    if (!plan) return null;

    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="mt-4 space-y-4"
      >
        {/* Header Card */}
        <Card className="border-2 border-green-200 bg-gradient-to-r from-green-50 to-amber-50">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Sprout className="w-6 h-6 text-green-600" />
                  <h3 className="text-xl font-bold text-green-800">{plan.title || "SAE 5E Lesson Plan"}</h3>
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {plan.grade_level && <Badge variant="secondary">{plan.grade_level}</Badge>}
                  {plan.content_area && <Badge className="bg-green-100 text-green-800">{plan.content_area}</Badge>}
                  {plan.standard && <Badge variant="outline">{plan.standard}</Badge>}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={copyToClipboard}>
                  <Copy className="w-4 h-4 mr-1" /> Copy
                </Button>
                <Button size="sm" variant="outline" onClick={downloadAsPDF}>
                  <Printer className="w-4 h-4 mr-1" /> Print
                </Button>
                <Button size="sm" variant="outline" onClick={downloadAsWord}>
                  <FileText className="w-4 h-4 mr-1" /> Word
                </Button>
                <Button size="sm" variant="default" className="bg-green-600 hover:bg-green-700" onClick={downloadAsJSON}>
                  <Download className="w-4 h-4 mr-1" /> Download
                </Button>
              </div>
            </div>

            {/* Key Concepts */}
            {plan.key_concepts && (
              <div className="mt-3 p-3 bg-white/70 rounded-lg">
                <p className="text-sm font-medium text-gray-500 mb-1">KEY CONCEPTS (Fruit)</p>
                <p className="text-gray-700">{safeRender(plan.key_concepts)}</p>
              </div>
            )}

            {/* Objectives */}
            {plan.sustainable_objective && (
              <div className="mt-3 p-3 bg-white/70 rounded-lg">
                <p className="text-sm font-medium text-gray-500 mb-1">SUSTAINABLE OBJECTIVE</p>
                <p className="text-gray-700"><strong>Students will be able to:</strong> {safeRender(plan.sustainable_objective)}</p>
              </div>
            )}

            {/* Vocabulary */}
            {plan.vocabulary && plan.vocabulary.length > 0 && (
              <div className="mt-3">
                <p className="text-sm font-medium text-gray-500 mb-2">KEY VOCABULARY (Seasonings)</p>
                <div className="flex flex-wrap gap-2">
                  {plan.vocabulary.map((v, i) => (
                    <Badge key={i} className="bg-amber-100 text-amber-800" title={typeof v === 'object' ? v.definition : ''}>
                      {typeof v === 'object' ? v.word : v}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 5E Phases */}
        <div className="space-y-2">
          <h4 className="font-semibold text-gray-600 flex items-center gap-2">
            <Target className="w-4 h-4" /> 5E Instructional Phases
          </h4>
          {renderPhaseSection("engage", plan.engage)}
          {renderPhaseSection("explore", plan.explore)}
          {renderPhaseSection("explain", plan.explain)}
          {renderPhaseSection("elaborate", plan.elaborate)}
          {renderPhaseSection("evaluate", plan.evaluate)}
        </div>

        {/* Materials */}
        {plan.materials && plan.materials.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <button
                onClick={() => toggleSection("materials")}
                className="w-full flex items-center justify-between"
              >
                <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                  <Leaf className="w-4 h-4 text-green-600" /> Materials & Tools
                </h4>
                {expandedSections.materials ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              <AnimatePresence>
                {expandedSections.materials && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="mt-3 grid grid-cols-2 gap-2"
                  >
                    {plan.materials.map((material, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
                        <input type="checkbox" className="rounded border-gray-300" />
                        <span>{material}</span>
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </CardContent>
          </Card>
        )}

        {/* SAE Lab & Home Learning */}
        {(plan.sae_lab || plan.home_learning) && (
          <Card>
            <CardContent className="p-4 space-y-4">
              {plan.sae_lab && (
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">In-Class SAE Lab</h4>
                  <p className="text-gray-600 text-sm">{safeRender(plan.sae_lab)}</p>
                </div>
              )}
              {plan.home_learning && (
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">Home Learning</h4>
                  <p className="text-gray-600 text-sm">{safeRender(plan.home_learning)}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Assessment */}
        {plan.assessment && (
          <Card className="border-red-200">
            <CardContent className="p-4">
              <h4 className="font-semibold text-red-700 flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-4 h-4" /> Assessment (Grow)
              </h4>
              {typeof plan.assessment === 'object' ? (
                <div className="text-gray-600 text-sm space-y-2">
                  {plan.assessment.rubric_type && <p><strong>Type:</strong> {plan.assessment.rubric_type}</p>}
                  {plan.assessment.criteria && Array.isArray(plan.assessment.criteria) && (
                    <div>
                      <p className="font-medium">Criteria:</p>
                      <ul className="list-disc list-inside ml-2">
                        {plan.assessment.criteria.map((c, i) => (
                          <li key={i}>{typeof c === 'object' ? c.skill || c.criterion : c}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-600 text-sm">{safeRender(plan.assessment)}</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Student Worksheets Summary */}
        {(plan.check_your_roots || plan.creative_worksheet || plan.test_your_growth || editedWorksheets) && (
          <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-purple-700 flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Student Worksheets
                </h4>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditMode(true)}
                    className="text-xs border-purple-300 text-purple-700 hover:bg-purple-50"
                  >
                    <Edit3 className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={printAnswerKey}
                    className="text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                  >
                    <Key className="w-3 h-3 mr-1" /> Answer Key
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="text-center p-2 bg-white rounded-lg border border-green-200">
                  <div className="text-lg mb-1">🌱</div>
                  <p className="text-xs font-medium text-green-700">Check Your Roots</p>
                  <p className="text-xs text-gray-500">Knowledge & SAE</p>
                </div>
                <div className="text-center p-2 bg-white rounded-lg border border-orange-200">
                  <div className="text-lg mb-1">🎨</div>
                  <p className="text-xs font-medium text-orange-700">Creative Activity</p>
                  <p className="text-xs text-gray-500">Think, Draw, Create</p>
                </div>
                <div className="text-center p-2 bg-white rounded-lg border border-red-200">
                  <div className="text-lg mb-1">📝</div>
                  <p className="text-xs font-medium text-red-700">Test Your Growth</p>
                  <p className="text-xs text-gray-500">Assessment</p>
                </div>
              </div>
              <p className="text-xs text-center text-purple-600 mt-3">
                Edit worksheets before printing, or print the Answer Key for grading!
              </p>
            </CardContent>
          </Card>
        )}
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 via-white to-amber-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                <Sprout className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-lg text-green-800">Growth Tool</h1>
                <p className="text-xs text-gray-500">AI Lesson Plan Generator</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {currentLessonPlan && (
              <Button variant="outline" size="sm" onClick={startNewPlan}>
                <RefreshCw className="w-4 h-4 mr-1" /> New Plan
              </Button>
            )}
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate("/teachers")}
            >
              <GraduationCap className="w-4 h-4 mr-1" /> Dashboard
            </Button>
          </div>
        </div>
      </header>

      {/* Main Chat Area */}
      <main className="max-w-5xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Chat Column */}
          <div className="flex flex-col h-[calc(100vh-180px)]">
            {/* Messages */}
            <div className="flex-1 overflow-y-auto space-y-4 pb-4">
              {messages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                      message.role === "user"
                        ? "bg-green-600 text-white rounded-br-md"
                        : "bg-white border shadow-sm rounded-bl-md"
                    }`}
                  >
                    {message.role === "assistant" && (
                      <div className="flex items-center gap-2 mb-2 pb-2 border-b">
                        <Sprout className="w-4 h-4 text-green-600" />
                        <span className="text-xs font-medium text-green-600">Growth Tool</span>
                        {message.isStreaming && (
                          <span className="flex items-center gap-1 text-xs text-amber-600 ml-auto">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Generating...
                          </span>
                        )}
                      </div>
                    )}
                    <p className={`whitespace-pre-wrap text-sm ${message.isStreaming ? 'font-mono text-xs bg-gray-50 p-2 rounded' : ''}`}>
                      {message.content}
                    </p>
                    <p className={`text-xs mt-2 ${message.role === "user" ? "text-green-100" : "text-gray-400"}`}>
                      {new Date(message.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                </motion.div>
              ))}
              
              {isLoading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-start"
                >
                  <div className="bg-white border shadow-sm rounded-2xl rounded-bl-md px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-green-600" />
                      <span className="text-sm text-gray-500">Creating your lesson plan...</span>
                    </div>
                  </div>
                </motion.div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="bg-white border rounded-2xl shadow-lg p-2">
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder="Start a lesson plan or challenge here..."
                  className="flex-1 resize-none border-0 focus:ring-0 focus:outline-none p-3 text-sm max-h-32 placeholder:text-gray-400"
                  rows={1}
                  style={{ minHeight: "44px" }}
                />
                <Button
                  onClick={sendMessage}
                  disabled={!input.trim() || isLoading}
                  className="bg-green-600 hover:bg-green-700 text-white rounded-xl h-11 w-11 p-0"
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </Button>
              </div>
              <div className="flex items-center gap-4 px-3 pt-2 text-xs text-gray-400">
                <span className="flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> AI-powered
                </span>
                <span className="flex items-center gap-1">
                  <FileText className="w-3 h-3" /> 5E Format
                </span>
                <span className="flex items-center gap-1">
                  <Leaf className="w-3 h-3" /> SAE Integrated
                </span>
              </div>
            </div>
          </div>

          {/* Lesson Plan Preview Column */}
          <div className="hidden lg:block h-[calc(100vh-180px)] overflow-y-auto">
            {currentLessonPlan ? (
              renderLessonPlan(currentLessonPlan)
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center p-8">
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4">
                  <BookOpen className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Your Lesson Plan Preview</h3>
                <p className="text-gray-500 text-sm max-w-xs">
                  Start a conversation to generate a complete SAE 5E lesson plan. It will appear here for review, editing, and download.
                </p>
                
                {/* Quick Start Examples */}
                <div className="mt-6 space-y-2 w-full max-w-sm">
                  <p className="text-xs font-medium text-gray-400 uppercase">Try these examples:</p>
                  {[
                    "CCSS.MATH.CONTENT.3.MD.C.5 - measuring garden bed areas",
                    "NGSS 3-LS1 - plant life cycles with bean seeds",
                    "VISAE Standard 3 - composting and soil health"
                  ].map((example, i) => (
                    <button
                      key={i}
                      onClick={() => setInput(example)}
                      className="w-full text-left p-3 rounded-lg bg-white border hover:border-green-300 hover:bg-green-50 transition-colors text-sm text-gray-600"
                    >
                      {example}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Lesson Plan (shows below chat on mobile) */}
        <div className="lg:hidden mt-6">
          {currentLessonPlan && renderLessonPlan(currentLessonPlan)}
        </div>
      </main>

      {/* Edit Worksheets Modal */}
      <AnimatePresence>
        {editMode && editedWorksheets && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={() => setEditMode(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto my-8"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white p-4 rounded-t-2xl z-10">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-lg flex items-center gap-2">
                      <Edit3 className="w-5 h-5" /> Edit Student Worksheets
                    </h3>
                    <p className="text-sm text-purple-100">Customize questions before printing</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={saveWorksheetEdits}
                      className="bg-white text-purple-700 hover:bg-purple-50"
                    >
                      <Save className="w-4 h-4 mr-1" /> Save Changes
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditMode(false)}
                      className="text-white hover:bg-white/20"
                    >
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-8">
                {/* Check Your Roots Section */}
                <div className="border-2 border-green-200 rounded-xl p-4">
                  <h4 className="font-bold text-green-700 text-lg mb-4 flex items-center gap-2">
                    🌱 Check Your Roots
                  </h4>
                  
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium">Agriculture Connection Question</Label>
                      <Textarea
                        value={editedWorksheets.check_your_roots.agriculture_connection_question}
                        onChange={(e) => updateWorksheetField("check_your_roots", "agriculture_connection_question", e.target.value)}
                        className="mt-1"
                        rows={2}
                      />
                    </div>
                    
                    <div>
                      <Label className="text-sm font-medium">Grow Your Knowledge - Directions</Label>
                      <Input
                        value={editedWorksheets.check_your_roots.grow_your_knowledge_directions}
                        onChange={(e) => updateWorksheetField("check_your_roots", "grow_your_knowledge_directions", e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-sm font-medium">Knowledge Questions</Label>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => addQuestion("check_your_roots", "grow_your_knowledge_questions")}
                          className="text-xs"
                        >
                          <Plus className="w-3 h-3 mr-1" /> Add Question
                        </Button>
                      </div>
                      {editedWorksheets.check_your_roots.grow_your_knowledge_questions.map((q, i) => (
                        <div key={i} className="flex gap-2 mb-2">
                          <span className="text-sm text-gray-500 mt-2">{i + 1}.</span>
                          <Input
                            value={q}
                            onChange={(e) => updateQuestion("check_your_roots", "grow_your_knowledge_questions", i, e.target.value)}
                            className="flex-1"
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeQuestion("check_your_roots", "grow_your_knowledge_questions", i)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                    
                    <div>
                      <Label className="text-sm font-medium">Home Learning Directions</Label>
                      <Input
                        value={editedWorksheets.check_your_roots.home_learning_directions}
                        onChange={(e) => updateWorksheetField("check_your_roots", "home_learning_directions", e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>

                {/* Creative Worksheet Section */}
                <div className="border-2 border-orange-200 rounded-xl p-4">
                  <h4 className="font-bold text-orange-700 text-lg mb-4 flex items-center gap-2">
                    🎨 Creative Activity
                  </h4>
                  
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium">Activity Directions</Label>
                      <Textarea
                        value={editedWorksheets.creative_worksheet.directions}
                        onChange={(e) => updateWorksheetField("creative_worksheet", "directions", e.target.value)}
                        className="mt-1"
                        rows={2}
                      />
                    </div>
                    
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-sm font-medium">Creative Prompts</Label>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => addQuestion("creative_worksheet", "prompts")}
                          className="text-xs"
                        >
                          <Plus className="w-3 h-3 mr-1" /> Add Prompt
                        </Button>
                      </div>
                      {editedWorksheets.creative_worksheet.prompts.map((p, i) => (
                        <div key={i} className="flex gap-2 mb-2">
                          <span className="text-sm text-gray-500 mt-2">{i + 1}.</span>
                          <Input
                            value={p}
                            onChange={(e) => updateQuestion("creative_worksheet", "prompts", i, e.target.value)}
                            className="flex-1"
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeQuestion("creative_worksheet", "prompts", i)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                    
                    <div>
                      <Label className="text-sm font-medium">Vocabulary Practice Instructions</Label>
                      <Input
                        value={editedWorksheets.creative_worksheet.vocabulary_practice}
                        onChange={(e) => updateWorksheetField("creative_worksheet", "vocabulary_practice", e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>

                {/* Test Your Growth Section */}
                <div className="border-2 border-red-200 rounded-xl p-4">
                  <h4 className="font-bold text-red-700 text-lg mb-4 flex items-center gap-2">
                    📝 Test Your Growth
                  </h4>
                  
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium">Test Instructions</Label>
                      <Textarea
                        value={editedWorksheets.test_your_growth.instructions}
                        onChange={(e) => updateWorksheetField("test_your_growth", "instructions", e.target.value)}
                        className="mt-1"
                        rows={2}
                      />
                    </div>
                    
                    {/* Multiple Choice */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-sm font-medium">Multiple Choice Questions</Label>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={addMCQuestion}
                          className="text-xs"
                        >
                          <Plus className="w-3 h-3 mr-1" /> Add Question
                        </Button>
                      </div>
                      {editedWorksheets.test_your_growth.multiple_choice.map((mc, i) => (
                        <div key={i} className="bg-gray-50 p-3 rounded-lg mb-3">
                          <div className="flex items-start gap-2 mb-2">
                            <span className="text-sm font-medium text-gray-500 mt-2">{i + 1}.</span>
                            <Input
                              value={mc.question}
                              onChange={(e) => updateMCQuestion(i, "question", e.target.value)}
                              placeholder="Question text"
                              className="flex-1"
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => removeMCQuestion(i)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          <div className="grid grid-cols-2 gap-2 ml-6">
                            {mc.options.map((opt, j) => (
                              <div key={j} className="flex items-center gap-1">
                                <span className={`text-xs font-medium ${String.fromCharCode(65 + j) === mc.correct_answer ? 'text-green-600' : 'text-gray-400'}`}>
                                  {String.fromCharCode(65 + j)}.
                                </span>
                                <Input
                                  value={opt}
                                  onChange={(e) => {
                                    const newOptions = [...mc.options];
                                    newOptions[j] = e.target.value;
                                    updateMCQuestion(i, "options", newOptions);
                                  }}
                                  placeholder={`Option ${String.fromCharCode(65 + j)}`}
                                  className="text-sm h-8"
                                />
                              </div>
                            ))}
                          </div>
                          <div className="ml-6 mt-2 flex items-center gap-2">
                            <Label className="text-xs text-gray-500">Correct Answer:</Label>
                            <select
                              value={mc.correct_answer}
                              onChange={(e) => updateMCQuestion(i, "correct_answer", e.target.value)}
                              className="text-sm border rounded px-2 py-1"
                            >
                              <option value="A">A</option>
                              <option value="B">B</option>
                              <option value="C">C</option>
                              <option value="D">D</option>
                            </select>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Short Answer */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-sm font-medium">Short Answer Questions</Label>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => addQuestion("test_your_growth", "short_answer")}
                          className="text-xs"
                        >
                          <Plus className="w-3 h-3 mr-1" /> Add Question
                        </Button>
                      </div>
                      {editedWorksheets.test_your_growth.short_answer.map((q, i) => (
                        <div key={i} className="flex gap-2 mb-2">
                          <span className="text-sm text-gray-500 mt-2">{i + 1}.</span>
                          <Input
                            value={q}
                            onChange={(e) => updateQuestion("test_your_growth", "short_answer", i, e.target.value)}
                            className="flex-1"
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeQuestion("test_your_growth", "short_answer", i)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                    
                    {/* Extended Response */}
                    <div>
                      <Label className="text-sm font-medium">Extended Response Question</Label>
                      <Textarea
                        value={editedWorksheets.test_your_growth.extended_response}
                        onChange={(e) => updateWorksheetField("test_your_growth", "extended_response", e.target.value)}
                        className="mt-1"
                        rows={3}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="sticky bottom-0 bg-gray-50 p-4 border-t rounded-b-2xl flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditMode(false)}>
                  Cancel
                </Button>
                <Button onClick={saveWorksheetEdits} className="bg-purple-600 hover:bg-purple-700 text-white">
                  <Save className="w-4 h-4 mr-1" /> Save & Close
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GrowthToolPage;
